import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Database, 
  MessageSquare, 
  Network, 
  BarChart3, 
  Brain,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Settings,
  User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import FileUploadNew from './FileUploadNew';
import ChatInterface from './ChatInterface';
import KnowledgeGraph from './KnowledgeGraph';
import Dashboard from './Dashboard';
import DocumentSelector from './DocumentSelector';
import LandingPage from './LandingPage';
import BookDemoPage from './BookDemoPage';
import KeyboardShortcuts from './KeyboardShortcuts';

type TabType = 'data' | 'chat' | 'knowledge-graph' | 'dashboard';
type ViewMode = 'micro' | 'macro' | 'files';

const NavigationInterface: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('data');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('micro');
  const { sections, documents, selectedDocIds, setSelectedDocIds, fetchFilteredSections } = useData();

  // Filter sections when selected documents change (must be before conditional returns)
  useEffect(() => {
    if (selectedDocIds.length > 0) {
      fetchFilteredSections(selectedDocIds);
    }
  }, [selectedDocIds, fetchFilteredSections]);

  // Check if we're on the demo page route
  const isDemoPage = window.location.pathname === '/demo';
  
  // If on demo page, show demo page
  if (isDemoPage) {
    return <BookDemoPage />;
  }

  // If not authenticated, show landing page with auth
  if (!loading && !user) {
    return <LandingPage onGetStarted={() => {}} showAuthButtons={true} />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center p-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-lg mb-4">
            <Brain className="h-12 w-12 text-white animate-pulse" />
          </div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'data' as TabType,
      label: 'Data',
      icon: Database,
      description: 'Upload and manage your documents'
    },
    {
      id: 'dashboard' as TabType,
      label: 'Dashboard',
      icon: BarChart3,
      description: 'Overview and analytics',
      disabled: !sections || sections.length === 0
    },
    {
      id: 'knowledge-graph' as TabType,
      label: 'Knowledge Graph',
      icon: Network,
      description: 'Explore document relationships',
      disabled: !sections || sections.length === 0
    },
    {
      id: 'chat' as TabType,
      label: 'Chat',
      icon: MessageSquare,
      description: 'Ask questions about your documents',
      disabled: !sections || sections.length === 0
    }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'data':
        return <FileUploadNew onDataLoaded={() => setActiveTab('dashboard')} />;
      case 'chat':
        return <ChatInterface />;
      case 'knowledge-graph':
        return (
          <KnowledgeGraph
            externalViewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        );
      case 'dashboard':
        return <Dashboard />;
      default:
        return <FileUploadNew onDataLoaded={() => setActiveTab('dashboard')} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed left-0 top-0 h-screen bg-white border-r border-gray-200 shadow-lg z-50 overflow-hidden"
          >
            <div className="flex flex-col h-full w-[280px]">
              {/* Logo & Brand */}
              <div className="p-5 border-b border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    {/* ScInter Logo - Two Interlocking Circles */}
                    <svg className="h-8 w-8" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="35" cy="50" r="22" fill="none" stroke="#1f2937" strokeWidth="6"/>
                      <circle cx="65" cy="50" r="22" fill="none" stroke="#1f2937" strokeWidth="6"/>
                    </svg>
                    <h1 className="text-xl font-bold text-black tracking-tight">
                      ScInter
                    </h1>
                  </div>
                  <motion.button
                    onClick={() => setSidebarOpen(false)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-black transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ChevronLeft size={18} />
                  </motion.button>
                </div>
                
                {/* User Profile Section */}
                {user && (
                  <div className="relative">
                    <button
                      onClick={() => setShowSettings(!showSettings)}
                      className="w-full flex items-center space-x-3 p-3 bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-xl border border-purple-100 hover:border-purple-200 hover:shadow-md transition-all group"
                    >
                      <div className="relative flex-shrink-0">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center text-white font-bold text-base shadow-lg">
                          {user.email?.charAt(0).toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-bold text-black truncate">
                          {user.fullName || 'User'}
                        </p>
                        <p className="text-xs text-gray-600 truncate">{user.email}</p>
                      </div>
                      <Settings size={16} className="text-gray-400 group-hover:text-purple-600 transition-colors flex-shrink-0" />
                    </button>
                    
                    {/* Settings Dropdown */}
                    <AnimatePresence>
                      {showSettings && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 right-0 mt-2 p-2 bg-white border border-gray-200 rounded-xl shadow-xl z-10"
                        >
                          <button
                            onClick={() => {
                              setShowSettings(false);
                              // Add profile functionality here
                            }}
                            className="w-full flex items-center space-x-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <User size={16} />
                            <span>Profile</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowSettings(false);
                              // Add settings functionality here
                            }}
                            className="w-full flex items-center space-x-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Settings size={16} />
                            <span>Settings</span>
                          </button>
                          <div className="my-1 border-t border-gray-200"></div>
                          <button
                            onClick={() => {
                              setShowSettings(false);
                              signOut();
                            }}
                            className="w-full flex items-center space-x-2 px-3 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <LogOut size={16} />
                            <span>Sign Out</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Navigation Label */}
              <div className="px-5 pt-6 pb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Navigation</p>
              </div>

              {/* Navigation Links */}
              <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const isDisabled = tab.disabled;

                  return (
                    <motion.button
                      key={tab.id}
                      onClick={() => !isDisabled && setActiveTab(tab.id)}
                      disabled={isDisabled}
                      className={`
                        w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200
                        ${isActive 
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                          : isDisabled
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : 'text-gray-700 hover:text-black hover:bg-gray-100'
                        }
                      `}
                      whileHover={!isDisabled ? { x: 4 } : {}}
                      whileTap={!isDisabled ? { scale: 0.98 } : {}}
                    >
                      <Icon size={20} className="flex-shrink-0" />
                      <span className="font-semibold text-sm">{tab.label}</span>
                      {isDisabled && (
                        <div className="ml-auto w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                      )}
                    </motion.button>
                  );
                })}
              </nav>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200">
                <p className="text-gray-500 text-xs text-center">
                  © 2025 ScInter · AI Platform
                </p>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Toggle Sidebar Button (Arrow) */}
      <motion.button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`fixed top-1/2 -translate-y-1/2 z-40 p-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white transition-all shadow-lg ${
          sidebarOpen ? 'left-[268px] rounded-r-lg' : 'left-0 rounded-r-lg'
        }`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={false}
        animate={{ left: sidebarOpen ? '268px' : '0px' }}
        transition={{ duration: 0.3 }}
      >
        {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
      </motion.button>

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-[280px]' : 'ml-0'}`}>
        {/* Document Selector - Show when documents are available and not on data page */}
        {documents.length > 0 && activeTab !== 'data' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-purple-50 via-white to-blue-50 border-b border-gray-200 px-6 py-4"
          >
            <div className="max-w-7xl mx-auto space-y-2">
              <DocumentSelector
                documents={documents}
                selectedDocIds={selectedDocIds}
                onSelectionChange={setSelectedDocIds}
                onApply={async (docIds) => {
                  // Apply the selection - this will trigger filtering
                  setSelectedDocIds(docIds);
                  // Fetch filtered sections for chat and KG
                  await fetchFilteredSections(docIds);
                  // The useEffect hook will handle refreshing the KG when selectedDocIds changes
                }}
                multiSelect={true}
              />
              {sections.length > 0 && (
                <div className="text-center text-sm text-gray-600 font-medium">
                  📄 {sections.length} section{sections.length !== 1 ? 's' : ''} from {selectedDocIds.length} document{selectedDocIds.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Page Content */}
        <main className="min-h-screen">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Keyboard Shortcuts */}
        <KeyboardShortcuts
          currentTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab as TabType)}
          onViewModeChange={setViewMode}
          currentViewMode={activeTab === 'knowledge-graph' ? viewMode : undefined}
        />
      </div>
    </div>
  );
};

export default NavigationInterface;



